import { useEffect, useState } from "react";
import { tierToRank } from "../lib/rarity";
import { openPhoto } from "./PhotoViewer";
import { CloseIcon, SceneFallback } from "../ui";
import { RarityPill } from "../ui/RarityPill";
import { sizedPhotoUrl } from "../lib/thumb";
import { hasRealCommonName } from "../../shared/species";
import { logEvent } from "../lib/events";

// Large image with the species info overlaid on a bottom scrim. Tapping the
// image opens the full-screen photo viewer; an optional Remove sits over the
// top-right corner.
export function SpeciesHero({
  photo_url,
  common_name,
  scientific_name,
  observation_count,
  chips = [],
  caption,
  onRemove,
  onOpen,
}: {
  photo_url: string;
  common_name: string;
  scientific_name: string;
  // Optional: when present, a rarity pill is shown. Surfaces (like the public
  // shared trip) that don't want a per-card rarity pill omit it.
  observation_count?: number;
  chips?: string[];
  // Extra muted line under the scientific name (e.g. the location logged).
  caption?: string;
  onRemove?: () => void;
  onOpen?: () => void;
}) {
  const rank =
    observation_count != null ? tierToRank(observation_count) : null;
  // When the merge-fallback echoed the scientific name into common_name
  // (no vernacular exists for this taxon), showing both lines duplicates
  // the same text bold+italic. Promote the scientific to the title in
  // italic and skip the subtitle line.
  const hasCommon = hasRealCommonName(common_name, scientific_name);
  const titleText = hasCommon ? common_name : scientific_name;
  // Track img decode failures so a broken iNat / Wikipedia URL degrades
  // to SceneFallback instead of the iOS default broken-image "?" glyph.
  // Reset when photo_url changes so a fresh source gets a fresh chance.
  const [errored, setErrored] = useState(false);
  useEffect(() => {
    setErrored(false);
  }, [photo_url]);
  const useScene = !photo_url || errored;

  // One-shot presentation breadcrumb per (photo_url, hasCommon) pair.
  // Deferred to an effect so we log post-render, not during render.
  useEffect(() => {
    if (useScene) {
      logEvent("presentation.photo.fallback", {
        surface: "hero",
        scientific_name,
        common_name,
        photo_url,
        reason: photo_url ? "load-error" : "no-url",
      });
    }
    if (!hasCommon) {
      logEvent("presentation.common_name.fallback", {
        surface: "hero",
        scientific_name,
        common_name,
      });
    }
    // Fire per photo_url change so repeated fallbacks for the same species
    // don't spam — only re-log when the URL or the mount changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photo_url, hasCommon, errored]);
  // Request iNat photos at `small` (~240 px) — the hero here renders around
  // a 4:3 aspect at typical phone widths, so `medium` would be wasted bytes.
  const shownUrl = photo_url ? sizedPhotoUrl(photo_url, "small") : "";

  return (
    <div className="relative overflow-hidden rounded-2xl bg-ink shadow-card ring-1 ring-inset ring-ink/10">
      <button
        type="button"
        onClick={() => {
          if (onOpen) onOpen();
          else if (photo_url && !errored)
            openPhoto({ url: photo_url, common_name, scientific_name });
        }}
        aria-label={
          onOpen
            ? `Open details for ${titleText}`
            : photo_url && !errored
              ? `Enlarge photo of ${titleText}`
              : titleText
        }
        className="block aspect-[4/3] w-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-aqua-deep"
      >
        {useScene ? (
          <SceneFallback
            hint={scientific_name}
            className="absolute inset-0"
            vignette
          />
        ) : (
          <img
            src={shownUrl}
            alt={common_name}
            loading="lazy"
            decoding="async"
            onError={() => setErrored(true)}
            className="h-full w-full object-cover"
          />
        )}
      </button>

      {/* scrim + info — §7.1 hero photo scrim exception */}
      <div className="scrim-hero pointer-events-none absolute inset-x-0 bottom-0 p-4 pt-10">
        {(rank || chips.length > 0) && (
          <div className="flex flex-wrap items-center gap-1.5">
            {rank && <RarityPill rank={rank} />}
            {chips.map((c) => (
              <span
                key={c}
                className="max-w-[12rem] truncate rounded-full bg-white/15 px-2 py-0.5 text-[10px] font-medium text-white/90 backdrop-blur-sm"
              >
                {c}
              </span>
            ))}
          </div>
        )}
        <p
          className={`mt-1.5 truncate font-display text-lg font-bold text-white${
            hasCommon ? "" : " italic"
          }`}
        >
          {titleText}
        </p>
        {hasCommon && (
          <p className="truncate text-sm italic text-white/65">
            {scientific_name}
          </p>
        )}
        {caption && (
          <p className="mt-0.5 truncate text-xs text-white/60">{caption}</p>
        )}
      </div>

      {onRemove && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          aria-label={`Remove ${titleText}`}
          className="absolute right-1.5 top-1.5 z-10 flex h-11 w-11 items-center justify-center rounded-full bg-ink/50 text-sm text-white/80 backdrop-blur-sm hover:bg-coral/80 hover:text-white"
        >
          <CloseIcon className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}
